import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ReactFlowProvider} from 'reactflow';

import {CanEditReturnValue} from 'analysis-tool/EditAnalysisHelpers';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import * as StubbableFunctions from 'processes/graph-builder-processes/flowchart-viz/util/stubbableFunctions';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import usePrevious from 'ui-library/hooks/usePrevious';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

import {AnalysisFlowchartAvailableAction} from './actions/AnalysisFlowchartActionTypes';
import {createGetActionInfoFn} from './actions/createGetActionInfoFn';
import {AnalysisGraph} from './analysis-graph/AnalysisGraphTypes';
import arrangeAnalysisGraph from './analysis-graph/arrangeAnalysisGraph';
import createAnalysisGraph from './analysis-graph/createAnalysisGraph';
import {
  AnalysisFlowchartConfig,
  AnalysisFlowchartContext,
  AnalysisFlowchartMode,
} from './AnalysisFlowchartContext';
import {ANALYSIS_FLOWCHART_BACKGROUND} from './AnalysisFlowchartStyleConstants';
import InternalAnalysisFlowchart from './InternalAnalysisFlowchart';

interface AnalysisFlowchartProps {
  analysis: Pick<PipelineGraphQL, 'id' | 'steps'>;
  /**
   * If `STATIC`, step statuses are hidden. If `INTERACTIVE`, step statuses are shown.
   */
  mode: AnalysisFlowchartMode;
  /**
   * Register which interactive actions are available.
   */
  availableActions?: Array<AnalysisFlowchartAvailableAction>;
  /**
   * Check if a step can be modified. If not passed, any step can be modified through any available action.
   *
   * `message` is shown in user-facing tooltips, to explain why the step cannot be modified.
   */
  canModifyStep?: (stepId: string) => CanEditReturnValue;
  /**
   * Check if the analysis as a whole can be modified. If not passed, the analysis can be modified by any action enabled in `availableActions`.
   *
   * `message` is shown in user-facing tooltips, to explain why the analysis cannot be modified.
   */
  canModifyAnalysis?: () => CanEditReturnValue;
  /**
   * Callback to refresh the pipeline, after an action.
   *
   * `refresh` is passed as a prop, because pipelines types have different GraphQL queries.
   */
  refresh?: () => Promise<unknown>;
  /**
   * Step IDs to visually highlight in the flowchart. Non-highlighted nodes are dimmed and the
   * viewport zooms to fit the highlighted nodes.
   */
  highlightedStepIds?: Array<string>;
  /**
   * The step ID whose side panel is currently open. When set, the corresponding node shows a
   * blue selection outline.
   */
  selectedStepId?: string | null;
}

/**
 * Visualize a pipeline as a flowchart, where each step in the pipeline is a node.
 *
 * In this module, `[Analysis]Flowchart` refers to React components and `[Analysis]Graph` refers to
 * the data structure used by those components.
 *
 * By default, this is a read-only component, but interactivity can be enabled through `availableActions`.
 *
 * This component is used by both Analyses or Analysis Templates.
 *
 * TODO(BNCH-101326): Rename component to PipelineFlowchart and move to appropriate directory
 */
export default function AnalysisFlowchart({
  analysis: pipeline,
  mode,
  highlightedStepIds,
  selectedStepId,
  ...rest
}: AnalysisFlowchartProps): JSX.Element {
  const {ref, width: reactFlowContainerWidth} = StubbableFunctions.useResizeObserver();

  const [arrangedGraph, setArrangedGraph] = useStateIfMounted<AnalysisGraph | null>(null);

  useEffect(() => {
    const arrangeGraph = async (): Promise<void> => {
      const arranged = await arrangeAnalysisGraph(createAnalysisGraph(pipeline));
      setArrangedGraph(arranged);
    };

    arrangeGraph();
  }, [pipeline, setArrangedGraph]);

  const {nodes, edges} = arrangedGraph || {nodes: [], edges: []};

  // Track whether the user has dismissed the highlights by interacting with the flowchart
  // (panning, zooming). Resets when new highlightedStepIds arrive from props.
  const [maybeHighlightsCleared, setHighlightsCleared] = useState(false);
  const prevHighlightedStepIds = usePrevious(highlightedStepIds);
  const highlightsCleared =
    prevHighlightedStepIds !== undefined && highlightedStepIds !== prevHighlightedStepIds
      ? false
      : maybeHighlightsCleared;

  const clearHighlights = useCallback(() => {
    setHighlightsCleared(true);
  }, []);

  // Prevent re-renders when using context; see react/jsx-no-constructed-context-values
  const highlightedStepIdsSet = useMemo(
    () => (highlightsCleared ? new Set<string>() : new Set(highlightedStepIds ?? [])),
    [highlightedStepIds, highlightsCleared]
  );
  const config: AnalysisFlowchartConfig = useMemo(
    () => ({
      mode,
      getActionInfo: createGetActionInfoFn({pipeline, ...rest}),
      highlightedStepIds: highlightedStepIdsSet,
      clearHighlights,
      selectedStepId: selectedStepId ?? null,
    }),
    [pipeline, mode, rest, highlightedStepIdsSet, clearHighlights, selectedStepId]
  );

  return (
    <div
      ref={ref}
      className={css(styles.flowchartWrapper)}
      {...getTestProps({component: 'AnalysisFlowChart', element: 'root'})}
    >
      <AnalysisFlowchartContext.Provider value={config}>
        <ReactFlowProvider>
          <ReactFlowWrapper
            width={reactFlowContainerWidth ?? 500}
            containerWidthForControls={reactFlowContainerWidth}
          >
            {() => (
              <div className={css(styles.flowchartWrapper)}>
                <InternalAnalysisFlowchart clientStateId={pipeline.id} nodes={nodes} edges={edges} />
              </div>
            )}
          </ReactFlowWrapper>
        </ReactFlowProvider>
      </AnalysisFlowchartContext.Provider>
    </div>
  );
}

const styles = StyleSheet.create({
  flowchartWrapper: {width: '100%', height: '100%', backgroundColor: ANALYSIS_FLOWCHART_BACKGROUND},
  floatingBanner: {
    borderRadius: 4,
    boxShadow: `0px 2px 8px 2px ${Colors.GRAY_LIGHT}`,
    position: 'absolute',
    marginLeft: 20,
    marginTop: 20,
    width: 425,
    zIndex: 10,
  },
});
